import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { ContentCopy, VpnKey } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api';
import DeviceDiagnosticsPage from '../DeviceDiagnosticsPage';
import GenerateEmergencyCodeModal from '../../components/workforceMonitoring/GenerateEmergencyCodeModal';
import ComputerOverviewPanel from '../../components/workforceMonitoring/ComputerOverviewPanel';
import LiveScreenViewer from '../../components/workforceMonitoring/LiveScreenViewer';
import RemoteSupportSessionsTab from '../../components/workforceMonitoring/RemoteSupportSessionsTab';
import ActivityPage from './ActivityPage';
import ApplicationsPage from './ApplicationsPage';
import WebActivityPage from './WebActivityPage';
import SessionsPage from './SessionsPage';
import CallMonitoringPanel from '../../components/workforceMonitoring/CallMonitoringPanel';

const BASE_TAB_KEYS = ['overview', 'diagnostics', 'activity', 'applications', 'web', 'sessions', 'call-monitoring', 'live-screen'] as const;

function operationalChipColor(status: string): 'success' | 'warning' | 'error' | 'default' | 'info' {
  switch (String(status || '').toUpperCase()) {
    case 'ONLINE':
      return 'success';
    case 'IDLE':
    case 'LOCKED':
      return 'info';
    case 'ATTENTION':
      return 'warning';
    case 'EMERGENCY ACCESS':
      return 'error';
    case 'OFFLINE':
    default:
      return 'default';
  }
}

const ComputerDetailPage: React.FC = () => {
  const { deviceId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tab = searchParams.get('tab') || 'overview';
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const permissions = user?.permissions || [];
  const canGenerate = permissions.includes('monitoring.emergency_access.generate');
  const canDiagnostics = permissions.includes('monitoring.diagnostics.read')
    || user?.role === 'super_user'
    || user?.role === 'administrator';
  const canViewLiveScreen = permissions.includes('monitoring.live_screen.view');
  const canStartRemoteControl = permissions.includes('monitoring.remote_control.start');
  const canReadRemoteControl = permissions.includes('monitoring.remote_control.read')
    || canStartRemoteControl;

  const { data: devicesData } = useQuery({
    queryKey: ['monitoringDevicesInventory'],
    queryFn: () => apiClient.monitoring.listDevices(),
    refetchInterval: 30000,
  });

  const { data: diagnosticsData } = useQuery({
    queryKey: ['deviceDiagnosticsBrief', deviceId],
    queryFn: () => apiClient.monitoring.getDeviceDiagnostics(deviceId),
    enabled: !!deviceId && canDiagnostics,
    retry: false,
  });

  const diagnostics = diagnosticsData?.data?.data;
  const devices = devicesData?.data?.data || [];
  const deviceRow = useMemo(
    () => devices.find((d: any) => d.id === deviceId || d.device_id === deviceId),
    [devices, deviceId],
  );
  const computerName = diagnostics?.device?.name
    || deviceRow?.device_name
    || 'Computer';
  const liveDeviceUid = deviceRow?.device_id || diagnostics?.device?.device_id || deviceId;

  const { data: rcStatusData } = useQuery({
    queryKey: ['remoteControlStatus', liveDeviceUid],
    queryFn: () => apiClient.monitoring.getRemoteControlStatus(liveDeviceUid),
    enabled: Boolean(liveDeviceUid) && canReadRemoteControl,
    refetchInterval: 30000,
  });

  const { data: rcConfigData } = useQuery({
    queryKey: ['monitoringRemoteControlConfig'],
    queryFn: () => apiClient.monitoring.getRemoteControlConfig(),
    enabled: canReadRemoteControl,
  });

  const employeeName = diagnostics?.employee
    ? `${diagnostics.employee.firstName || ''} ${diagnostics.employee.lastName || ''}`.trim()
    : deviceRow?.employee?.name;
  const campaignName = diagnostics?.assignment?.current_campaign || deviceRow?.campaign || null;
  const online = diagnostics?.device?.online ?? deviceRow?.online;
  const statusLabel = deviceRow?.operational_status || (online ? 'ONLINE' : 'OFFLINE');
  const heartbeat = deviceRow?.last_heartbeat_label
    || diagnostics?.device?.last_heartbeat_label
    || 'Unknown';
  const employeeLabel = employeeName || 'Unassigned';
  const campaignLabel = typeof campaignName === 'string'
    ? campaignName
    : (campaignName?.name || deviceRow?.campaign || '—');
  const statusAccent = online ? 'success.main' : 'grey.400';
  const remoteControlGlobalEnabled = rcConfigData?.data?.data != null
    ? rcConfigData.data.data.enabled === true
    : deviceRow?.remote_control_global_enabled === true;
  const remoteControlDeviceEnabled = rcStatusData?.data?.data != null
    ? rcStatusData.data.data.remote_control_enabled === true
    : deviceRow?.remote_control_device_enabled === true;
  const remoteControlLabel = !canReadRemoteControl
    ? null
    : !remoteControlGlobalEnabled
      ? 'Remote Control · Disabled globally'
      : remoteControlDeviceEnabled
        ? 'Remote Control · Available'
        : 'Remote Control · Disabled';

  const setTab = (nextTab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', nextTab);
    setSearchParams(next);
  };

  const copyDeviceId = async () => {
    try {
      await navigator.clipboard.writeText(deviceId);
    } catch {
      // ignore
    }
  };

  // Structured Web Activity is release-gated; hide its sub-tab unless the feature is enabled.
  const { data: monitoringConfigData } = useQuery({
    queryKey: ['monitoring', 'configuration', 'nav'],
    queryFn: () => apiClient.monitoring.getConfiguration(),
    staleTime: 60_000,
  });
  const webActivityEnabled = Boolean((monitoringConfigData as any)?.data?.data?.web_activity_feature_enabled);

  const tabKeys = useMemo(() => {
    let keys: string[] = [...BASE_TAB_KEYS];
    if (!webActivityEnabled) {
      keys = keys.filter((k) => k !== 'web');
    }
    if (canReadRemoteControl) {
      keys.push('remote-support');
    }
    return keys;
  }, [canReadRemoteControl, webActivityEnabled]);

  const tabLabel = (key: string) => {
    if (key === 'web') return 'Web Activity';
    if (key === 'live-screen') return 'Live Screen';
    if (key === 'remote-support') return 'Remote Support';
    if (key === 'call-monitoring') return 'Call Monitoring';
    return key.charAt(0).toUpperCase() + key.slice(1);
  };

  return (
    <Box>
      <Button size="small" onClick={() => navigate('/workforce-monitoring/computers')} sx={{ mb: 1.5 }}>
        ← Computers
      </Button>

      <Box
        sx={{
          mb: 2,
          px: 2,
          py: 1.75,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          borderLeft: '4px solid',
          borderLeftColor: statusAccent,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
          <Box minWidth={0}>
            <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap" mb={0.75}>
              <Typography variant="h5" fontWeight={800} letterSpacing={0.2} sx={{ lineHeight: 1.2 }}>
                {computerName}
              </Typography>
              <Chip
                size="small"
                color={operationalChipColor(statusLabel)}
                label={statusLabel}
                sx={{ fontWeight: 700, letterSpacing: 0.4 }}
              />
              {remoteControlLabel && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={remoteControlGlobalEnabled && remoteControlDeviceEnabled ? 'success' : 'default'}
                  label={remoteControlLabel}
                />
              )}
            </Box>
            <Typography variant="body1" fontWeight={600} sx={{ mb: 0.25 }}>
              {employeeLabel}
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}> · {campaignLabel}</Box>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
              Last heartbeat {heartbeat}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="caption" color="text.secondary">
                Device ID: {deviceId}
              </Typography>
              <Tooltip title="Copy Device ID">
                <IconButton size="small" onClick={copyDeviceId} aria-label="Copy device id">
                  <ContentCopy fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {canGenerate && (
            <Button variant="contained" startIcon={<VpnKey />} onClick={() => setEmergencyOpen(true)}>
              Generate Emergency Code
            </Button>
          )}
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{
          mb: 2,
          px: 1,
          borderRadius: 2,
          bgcolor: 'grey.100',
          border: '1px solid',
          borderColor: 'divider',
          minHeight: 48,
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
          '& .MuiTab-root': {
            minHeight: 48,
            textTransform: 'none',
            fontWeight: 600,
            color: 'text.secondary',
            '&.Mui-selected': {
              color: 'primary.main',
              fontWeight: 700,
            },
          },
        }}
      >
        {tabKeys.map((key) => (
          <Tab key={key} label={tabLabel(key)} value={key} />
        ))}
      </Tabs>

      {tab === 'diagnostics' ? (
        canDiagnostics ? (
          <DeviceDiagnosticsPage embedded />
        ) : (
          <Typography color="text.secondary">
            Diagnostics require the monitoring.diagnostics.read permission.
          </Typography>
        )
      ) : tab === 'live-screen' ? (
        canViewLiveScreen ? (
          <LiveScreenViewer
            deviceId={liveDeviceUid}
            computerName={computerName}
            employeeName={employeeName}
            campaignName={typeof campaignName === 'string' ? campaignName : campaignName?.name}
            workState={deviceRow?.work_state || diagnostics?.work_session?.work_state}
            clockStatus={deviceRow?.clock_status || diagnostics?.work_session?.clock_status}
            liveScreenDeviceEnabled={deviceRow?.live_screen_device_enabled !== false}
            liveScreenGlobalEnabled={deviceRow?.live_screen_global_enabled !== false}
            liveScreenStatusLabel={deviceRow?.live_screen_status_label}
            remoteControlDeviceEnabled={remoteControlDeviceEnabled}
            remoteControlGlobalEnabled={remoteControlGlobalEnabled}
          />
        ) : (
          <Typography color="text.secondary">
            Live Screen requires the monitoring.live_screen.view permission.
          </Typography>
        )
      ) : tab === 'remote-support' ? (
        canReadRemoteControl ? (
          <RemoteSupportSessionsTab deviceId={liveDeviceUid || deviceId} hideComputerColumn />
        ) : (
          <Typography color="text.secondary">
            Remote Support history requires the monitoring.remote_control.read permission.
          </Typography>
        )
      ) : tab === 'overview' ? (
        <ComputerOverviewPanel deviceId={deviceId} />
      ) : tab === 'activity' ? (
        <ActivityPage scope="computer" lockedDeviceId={liveDeviceUid || deviceId} />
      ) : tab === 'applications' ? (
        <ApplicationsPage scope="computer" lockedDeviceId={liveDeviceUid || deviceId} />
      ) : (tab === 'web' && webActivityEnabled) ? (
        <WebActivityPage scope="computer" lockedDeviceId={liveDeviceUid || deviceId} />
      ) : tab === 'sessions' ? (
        <SessionsPage scope="computer" lockedDeviceId={liveDeviceUid || deviceId} />
      ) : tab === 'call-monitoring' ? (
        <CallMonitoringPanel deviceId={deviceId} />
      ) : null}

      <GenerateEmergencyCodeModal
        open={emergencyOpen}
        onClose={() => setEmergencyOpen(false)}
        target={{
          employeeId: diagnostics?.employee?.id || deviceRow?.employee?.id,
          employeeName: employeeName,
          deviceId,
          deviceName: computerName,
          campaignName: typeof campaignName === 'string' ? campaignName : campaignName?.name,
        }}
      />
    </Box>
  );
};

export default ComputerDetailPage;
